<template>
    <v-card>
        <v-card-title>
            Authorized Individual(s)
        </v-card-title>
        <v-card-text>
            <v-alert :color="infoColor" prominent text type="info">
                <div class="font-weight-bold">Important Information</div>
                <ul>
                    <li>This release of information applies to information or services available from the
                        Residence Office.
                    </li>
                    <li> In the event we need to contact your Authorized Individual for clarification of a request,
                        please provide a telephone number and/or email address for your Authorized Individual.</li>
                </ul>
            </v-alert>
            <v-alert v-if="alert" :color="dangerColor" prominent text type="warning">
                {{ alertMessage }}
            </v-alert>
            <v-divider class="pt-2 pb-2"></v-divider>
            <h3 class="pb-2">The following is a list of individual(s) you have authorized to have access to the
                specific personal information.</h3>
            <div class="pb-3">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" :color="infoColor" @click="addUser()" dark>
                            <v-icon left>mdi-account-plus</v-icon> Add Authorized Individual
                        </v-btn>
                    </template>
                    <span>Add Authorized Individual</span>
                </v-tooltip>
            </div>
            <v-alert v-if="contacts.length == 0" :color="successColor" dense icon="mdi-account-plus" text>
                There are currently no authorized individual(s). Please add at least one authorized individual.
            </v-alert>
            <v-expansion-panels v-model="panels" multiple>
                <v-expansion-panel v-for="(user,i) in contacts" :key="i">
                    <v-expansion-panel-header class="white--text" :color="kingsGreen">Authorized Individual {{ i + 1 }}
                        - {{ user.name }}
                        <template v-slot:actions>
                            <v-icon color="#fff">$expand</v-icon>
                        </template>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-form ref="detailForm" v-model="valid" lazy-validation>
                            <v-container fluid>
                                <v-row>
                                    <v-col cols="12" lg="4" class="pb-0">
                                        <v-text-field v-model="user.name" label="Authorized Individual's Name"
                                            :rules="nameRules" required>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" lg="4" class="pb-0">
                                        <v-select v-model="user.relationship" :items="relationshipItems"
                                            label="Relationship" :rules="relationshipRules" required>
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" lg="4" class="pb-0">
                                        <v-text-field v-model="user.relationshipDetail"
                                            :disabled="user.relationship != 'other'" label="Relationship (Other)">
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" lg="6" class="pb-0">
                                        <v-text-field v-model="user.phone" label="Telephone Number" :rules="phoneRules"
                                            required>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" lg="6" class="pb-0">
                                        <v-text-field v-model="user.mail" label="Email Address" :rules="mailRules"
                                            required>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" lg="6">
                                        <v-menu ref="startDate" v-model="user.startDateMenu"
                                            transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field v-model="user.startDate" :color="successColor"
                                                    label="Authorization Start Date" prepend-icon="mdi-calendar-check"
                                                    readonly :rules="startDateRules" v-bind="attrs" v-on="on">
                                                </v-text-field>
                                            </template>
                                            <v-date-picker v-model="user.startDate" :max="user.endDate" no-title
                                                @input="user.startDateMenu = false">
                                            </v-date-picker>
                                        </v-menu>
                                    </v-col>
                                    <v-col cols="12" lg="6">
                                        <v-menu ref="endDate" v-model="user.endDateMenu" transition="scale-transition"
                                            offset-y max-width="290px" min-width="290px">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field v-model="user.endDate" :color="dangerColor"
                                                    label="Authorization End Date" prepend-icon="mdi-calendar-remove"
                                                    readonly :rules="endDateRules" v-bind="attrs" v-on="on">
                                                </v-text-field>
                                            </template>
                                            <v-date-picker v-model="user.endDate" :min="user.startDate" no-title
                                                @input="user.endDateMenu = false">
                                            </v-date-picker>
                                        </v-menu>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" class="pb-0">
                                        <div class="font-weight-bold">Authorized Method of Access</div>
                                        <div>Please indicate how you authorize this person to access the
                                            specific information (Check all that
                                            apply).</div>
                                        <v-checkbox v-model="user.accessMethod" dense hide-details label="By Phone"
                                            :rules="accessMethodRules" value="phone">
                                        </v-checkbox>
                                        <v-checkbox v-model="user.accessMethod" dense hide-details label="In Person"
                                            :rules="accessMethodRules" value="inperson">
                                        </v-checkbox>
                                        <v-checkbox v-model="user.accessMethod" dense label="Email"
                                            :rules="accessMethodRules" value="email">
                                        </v-checkbox>
                                        <v-text-field v-model="user.accessCode"
                                            hint="Suggestion: Authorized individual's date of birth"
                                            label="Authorized Individual's Code Word" persistent-hint :rules="codeRules"
                                            required single-line>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" class="pb-0">
                                        <div class="font-weight-bold">Access Residence Information</div>
                                        <div>Please indicate what your authorized person has access to (Check all that
                                            apply).</div>
                                        <v-checkbox v-model="user.accessResidenceInformation" dense hide-details
                                            label="Room Location" :rules="accessResidenceInformationRules"
                                            value="room location">
                                        </v-checkbox>
                                        <v-checkbox v-model="user.accessResidenceInformation" dense hide-details
                                            label="Mailbox Number" :rules="accessResidenceInformationRules"
                                            value="mailbox number">
                                        </v-checkbox>
                                        <v-checkbox v-model="user.accessResidenceInformation" dense hide-details
                                            label="Residence Financial Account" :rules="accessResidenceInformationRules"
                                            value="residence financial account">
                                        </v-checkbox>
                                        <v-checkbox v-model="user.accessResidenceInformation" dense
                                            label="Resident Conduct File" :rules="accessResidenceInformationRules"
                                            value="residence conduct file">
                                        </v-checkbox>
                                    </v-col>
                                    <v-col cols="12" class="pb-0">
                                        <div class="font-weight-bold">Emergency Contact</div>
                                        <div>Please indicate if this authorized person may be contacted on your behalf
                                            in the event of an emergency.</div>
                                        <v-radio-group v-model="user.emergencyContact">
                                            <v-radio label="Yes" value="1"></v-radio>
                                            <v-radio label="No" value="0"></v-radio>
                                        </v-radio-group>
                                    </v-col>
                                    <v-col cols="12">
                                        <v-btn :color="dangerColor" dark @click="removeUser(i)">
                                            <v-icon left>mdi-account-remove</v-icon> Remove Authorized Individual
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-form>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
            <v-spacer>
            </v-spacer>
            <v-btn class="white--text" :color="successColor" :disabled="!valid" @click="nextStep(3)">
                Next
            </v-btn>
            <v-btn class="white--text" :color="kingsGrey" @click="setStep(1)">
                Previous
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
export default {
    name: 'release-of-information-detail',
    props: {
        contacts: {
            required: true,
            type: Array
        },
        panels: {
            required: true,
            type: Array
        }
    },
    data() {
        return {
            alert: false,
            alertMessage: '',
            accessMethodRules: [
                v => !!v || 'Access method is required',
                v => (v && v.length >= 1) || 'At least one access method must be selected',
            ],
            accessResidenceInformationRules: [
                v => !!v || 'Access residence is required',
                v => (v && v.length >= 1) || 'At least one access residence must be selected',
            ],
            codeRules: [
                v => !!v || 'Code is required',
                v => (v && v.length >= 2) || 'Code must be more than 2 characters'
            ],
            endDateRules: [
                v => !!v || 'End date is required',
            ],
            nameRules: [
                v => !!v || 'Name is required',
                v => (v && v.length >= 2) || 'Name must be more than 2 characters'
            ],
            mailRules: [
                v => !!v || 'Email is required',
            ],
            panel: [],
            phoneRules: [
                v => !!v || 'Telephone is required',
            ],
            relationshipItems: [{
                    disabled: true,
                    text: 'Please select...',
                    value: ''
                }, {
                    text: 'Parent',
                    value: 'parent'
                },
                {
                    text: 'Guardian',
                    value: 'guardian'
                },
                {
                    text: 'Sibling',
                    value: 'sibling'
                },
                {
                    text: 'Friend',
                    value: 'friend'
                },
                {
                    text: 'Other',
                    value: 'other'
                }
            ],
            relationshipRules: [
                v => !!v || 'Relationship is required'
            ],
            startDateRules: [
                v => !!v || 'Start date is required',
            ],
            valid: true
        }
    },
    methods: {
        addUser() {
            this.$emit('addPanel');
            this.$emit('addUser');
        },
        removeUser(i) {
            this.$emit('removePanel');
            this.$emit('removeUser', i);
        },
        nextStep(val) {
            if (this.contacts.length > 0) {
                // Disable alert
                this.alert = false;

                // Validate forms
                for (let i = 0; i < this.$refs.detailForm.length; i++) {
                    if (!this.$refs.detailForm[i].validate()) {
                        this.valid = false;
                    }
                }

                // Proceed if forms are valid
                if (this.valid) {
                    this.setStep(val);
                } else {
                    this.alert = true;
                    this.alertMessage = 'Please verify that all required form fields for each authorized individual is valid.';
                }
            } else {
                this.alert = true;
                this.alertMessage = `At least one authorized individual is required. Please click the 'add authorized individual' button to add a authorized individual.`;
            }
        },
        setStep(val) {
            this.$emit('resetPanel');
            this.$emit('setStep', val);
            this.$nextTick(() => {
                this.$emit('scrollToTop');
            })
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        infoColor() {
            return '#5578eb';
        },
        kingsGreen() {
            return '#004236';
        },
        kingsGrey() {
            return '#6d6e71';
        },
        successColor() {
            return '#1bc5bd';
        }
    }
}
</script>

<style scoped>
.v-input--checkbox {
    margin-top: 0;
}
</style>